import {
  Building06Icon,
  FaceIdIcon,
  Delete01Icon,
  MailReceive02Icon,
  MailSend02Icon,
  MessageIncoming01Icon,
  MessageOutgoing01Icon,
  MessageQuestionIcon,
  ArrowRight03Icon,
  Note01Icon,
  NoteEditIcon,
  Pen01Icon,
  PencilEdit02Icon,
  UserCircle02Icon,
  ViewIcon,
  Calendar04Icon,
  CalendarFavorite01Icon,
  CallIcon,
  CallIncoming02Icon,
  CallOutgoing02Icon,
  AttachmentIcon,
  Key01Icon,
  MailAdd01Icon,
  MailBlock01Icon,
  PassportExpiredIcon,
  SentIcon as SentOutlineIcon,
  Tag01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  isMissedInboundCallActivity,
  LeadActivity,
  LeadActivityType
} from "views/leads-id/types";
import { PropertyManagementSystem } from "shared/types/partner";
import {
  AddCircleIcon,
  AlertCircleIcon,
  CancelCircleIcon,
  CheckmarkCircle02Icon,
  Clock01Icon,
  RefreshIcon,
  StatusIcon,
  TimeQuarterPassIcon,
  SentIcon,
  UploadCircle01Icon
} from "@hugeicons-pro/core-solid-rounded";
import { ReactNode } from "react";
import { Show } from "shared/components/helpers/Show";
import { cx } from "shared/utils";
import agentProfileSvg from "shared/icons/boomLeasingAgentProfile.svg";
import { isAiAgentActor } from "./activity-attribution";
import { INTEGRATION_LOGO } from "views/settings-integration/constants";

const PMS_PUSH_ACTIVITY_TYPES = new Set<string>([
  LeadActivityType.PUSH_LEAD_TO_PMS_SUCCESS,
  LeadActivityType.PUSH_LEAD_TO_PMS_FAILED
]);

const iconMap: Partial<Record<LeadActivityType, ReactNode>> = {
  lead_created_by_user: <HugeiconsIcon size={20} icon={ViewIcon} />,
  stage_changed_auto: <HugeiconsIcon size={20} icon={StatusIcon} />,
  stage_changed_manual: <HugeiconsIcon size={20} icon={ViewIcon} />,
  file_uploaded: <HugeiconsIcon size={20} icon={AttachmentIcon} />,
  file_deleted: <HugeiconsIcon size={20} icon={Delete01Icon} />,
  note_added: <HugeiconsIcon size={20} icon={Note01Icon} />,
  email_invite_sent: <HugeiconsIcon size={20} icon={ViewIcon} />,
  lead_created_auto: <HugeiconsIcon size={20} icon={ViewIcon} />,
  lead_details_edited: <HugeiconsIcon size={20} icon={PencilEdit02Icon} />,
  identity_verification_passed: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={FaceIdIcon} stroke="none" />
      <div className="absolute -bottom-1 -right-1 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={CheckmarkCircle02Icon}
          size={16}
          className="text-success-500"
        />
      </div>
    </div>
  ),
  identity_verification_passed_with_warnings: (
    <HugeiconsIcon size={20} icon={ViewIcon} />
  ),
  identity_verification_failed: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={FaceIdIcon} stroke="none" />
      <div className="absolute -bottom-1 -right-1 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={CancelCircleIcon}
          size={16}
          className="text-danger-500"
        />
      </div>
    </div>
  ),
  identity_verification_overridden: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={FaceIdIcon} stroke="none" />
      <div className="absolute -bottom-1 -right-1 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={Pen01Icon}
          size={16}
          className="text-content-1000"
          stroke="none"
        />
      </div>
    </div>
  ),
  identity_verification_retry_requested: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={FaceIdIcon} stroke="none" />
      <div className="absolute -bottom-1 -right-1 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={RefreshIcon}
          size={16}
          className="text-content-1000"
        />
      </div>
    </div>
  ),
  identity_verification_retry_completed: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={FaceIdIcon} stroke="none" />
      <div className="absolute -bottom-1 -right-1 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={CheckmarkCircle02Icon}
          size={16}
          className="text-success-500"
        />
      </div>
    </div>
  ),
  identity_verification_expired: (
    <HugeiconsIcon size={20} icon={PassportExpiredIcon} />
  ),
  lead_assigned: <HugeiconsIcon size={20} icon={UserCircle02Icon} />,
  tag_added: <HugeiconsIcon size={20} icon={Tag01Icon} />,
  tag_removed: <HugeiconsIcon size={20} icon={Tag01Icon} />,
  identity_verification_data_edited: (
    <HugeiconsIcon size={20} icon={FaceIdIcon} />
  ),
  prescreening_submitted: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={MessageQuestionIcon} />
      <div className="absolute -bottom-1.5 -right-1.5 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={CheckmarkCircle02Icon}
          size={16}
          className="text-success-500"
        />
      </div>
    </div>
  ),
  prescreening_completed: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={MessageQuestionIcon} />
      <div className="absolute -bottom-1.5 -right-1.5 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={CheckmarkCircle02Icon}
          size={16}
          className="text-success-500"
        />
      </div>
    </div>
  ),
  prescreening_failed: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={MessageQuestionIcon} />
      <div className="absolute -bottom-1.5 -right-1.5 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={CancelCircleIcon}
          size={16}
          className="text-danger-500"
        />
      </div>
    </div>
  ),
  prescreening_abandoned: (
    <div className="relative">
      <div className="w-6 h-6 rounded-full border-2 border-content-1000 flex items-center justify-center">
        <HugeiconsIcon size={14} icon={ArrowRight03Icon} strokeWidth={2.5} />
      </div>
      <div className="absolute -bottom-3 -right-3 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={AlertCircleIcon}
          size={16}
          className="text-warning-600"
        />
      </div>
    </div>
  ),
  email_received: <HugeiconsIcon size={20} icon={MailReceive02Icon} />,
  new_inquiry: <HugeiconsIcon size={20} icon={MessageQuestionIcon} />,
  email_sent: <HugeiconsIcon size={20} icon={MailSend02Icon} />,
  email_unsubscribed: <HugeiconsIcon size={20} icon={MailBlock01Icon} />,
  email_resubscribed: <HugeiconsIcon size={20} icon={MailAdd01Icon} />,
  sms_received: <HugeiconsIcon size={20} icon={MessageIncoming01Icon} />,
  sms_sent: <HugeiconsIcon size={20} icon={MessageOutgoing01Icon} />,
  campaign_email_sent: <HugeiconsIcon size={20} icon={MailSend02Icon} />,
  campaign_sms_sent: <HugeiconsIcon size={20} icon={MessageOutgoing01Icon} />,
  sms_not_sent: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={MessageOutgoing01Icon} />
      <div className="absolute -bottom-1 -right-1 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={CancelCircleIcon}
          size={16}
          className="text-danger-500"
        />
      </div>
    </div>
  ),
  manual_record: <HugeiconsIcon size={20} icon={NoteEditIcon} />,
  showing_invite_sent: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={Calendar04Icon} />
      <div className="absolute -bottom-1.5 -right-1.5 bg-background-800 rounded-full">
        <HugeiconsIcon icon={SentIcon} size={16} className="text-primary-500" />
      </div>
    </div>
  ),
  showing_requested: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={Calendar04Icon} />
      <div className="absolute -bottom-1.5 -right-1.5 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={Clock01Icon}
          size={16}
          className="text-warning-600"
        />
      </div>
    </div>
  ),
  showing_scheduled: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={Calendar04Icon} />
      <div className="absolute -bottom-1.5 -right-1.5 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={AddCircleIcon}
          size={16}
          className="text-primary-500"
        />
      </div>
    </div>
  ),
  showing_confirmed: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={Calendar04Icon} />
      <div className="absolute -bottom-1.5 -right-1.5 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={CheckmarkCircle02Icon}
          size={16}
          className="text-success-500"
        />
      </div>
    </div>
  ),
  ai_call_received: <HugeiconsIcon size={20} icon={CallIcon} />,
  outbound_call_placed: <HugeiconsIcon size={20} icon={CallOutgoing02Icon} />,
  inbound_call_received: <HugeiconsIcon size={20} icon={CallIncoming02Icon} />,
  inbound_call_forwarded: <HugeiconsIcon size={20} icon={CallIncoming02Icon} />,
  inbound_call_forward_failed: (
    <HugeiconsIcon size={20} icon={CallIncoming02Icon} />
  ),
  inbound_call_text_only: <HugeiconsIcon size={20} icon={CallIncoming02Icon} />,
  showing_checked_in: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={Calendar04Icon} />
      <div className="absolute -bottom-1.5 -right-1.5 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={TimeQuarterPassIcon}
          size={16}
          className="text-content-800"
        />
      </div>
    </div>
  ),
  showing_checked_out: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={Calendar04Icon} />
      <div className="absolute -bottom-1.5 -right-1.5 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={CheckmarkCircle02Icon}
          size={16}
          className="text-success-500"
        />
      </div>
    </div>
  ),
  showing_feedback_submitted: (
    <HugeiconsIcon size={20} icon={CalendarFavorite01Icon} />
  ),
  showing_guided_confirmed: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={Calendar04Icon} />
      <div className="absolute -bottom-1.5 -right-1.5 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={TimeQuarterPassIcon}
          size={16}
          className="text-content-800"
        />
      </div>
    </div>
  ),
  showing_rescheduled: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={Calendar04Icon} />
      <div className="absolute -bottom-1.5 -right-1.5 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={RefreshIcon}
          size={16}
          className="text-danger-500"
        />
      </div>
    </div>
  ),
  showing_lockbox_not_configured: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={Calendar04Icon} />
      <div className="absolute -bottom-1.5 -right-1.5 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={AlertCircleIcon}
          size={16}
          className="text-warning-600"
        />
      </div>
    </div>
  ),
  showing_cancelled: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={Calendar04Icon} />
      <div className="absolute -bottom-1.5 -right-1.5 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={CancelCircleIcon}
          size={16}
          className="text-danger-500"
        />
      </div>
    </div>
  ),
  showing_completed: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={Calendar04Icon} />
      <div className="absolute -bottom-1.5 -right-1.5 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={CheckmarkCircle02Icon}
          size={16}
          className="text-success-500"
        />
      </div>
    </div>
  ),
  showing_no_show: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={Calendar04Icon} />
      <div className="absolute -bottom-1.5 -right-1.5 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={Clock01Icon}
          size={16}
          className="text-warning-600"
        />
      </div>
    </div>
  ),
  showing_not_checked_in: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={Calendar04Icon} />
      <div className="absolute -bottom-1.5 -right-1.5 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={CancelCircleIcon}
          size={16}
          className="text-danger-600"
        />
      </div>
    </div>
  ),
  showing_reassigned: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={Calendar04Icon} />
      <div className="absolute -bottom-1.5 -right-1.5 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={Pen01Icon}
          size={16}
          className="text-content-700"
        />
      </div>
    </div>
  ),
  showing_serial_number_incorrect: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={Key01Icon} />
      <div className="absolute -bottom-1.5 -right-1.5 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={AlertCircleIcon}
          size={16}
          className="text-warning-600"
        />
      </div>
    </div>
  ),
  showing_serial_number_lockout: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={Key01Icon} />
      <div className="absolute -bottom-1.5 -right-1.5 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={CancelCircleIcon}
          size={16}
          className="text-danger-500"
        />
      </div>
    </div>
  ),
  tenant_notified_of_showing: (
    <div className="relative">
      <HugeiconsIcon size={20} icon={SentOutlineIcon} />
      <div className="absolute -bottom-1.5 -right-1.5 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={UploadCircle01Icon}
          size={16}
          className="text-primary-500"
        />
      </div>
    </div>
  )
};

const PmsLogoContent = ({
  logo,
  pmsType
}: {
  logo: (typeof INTEGRATION_LOGO)[PropertyManagementSystem];
  pmsType?: string;
}) => {
  if (logo?.Icon) return <logo.Icon className="size-full" />;
  if (logo?.src)
    return (
      <img src={logo.src} alt={pmsType} className="size-full object-contain" />
    );
  return <HugeiconsIcon size={16} icon={Building06Icon} />;
};

const PmsIcon = ({
  activityType,
  pmsType
}: {
  activityType: string;
  pmsType?: string;
}) => {
  const logo = pmsType
    ? INTEGRATION_LOGO[pmsType as PropertyManagementSystem]
    : undefined;
  const isSuccess = activityType === LeadActivityType.PUSH_LEAD_TO_PMS_SUCCESS;

  return (
    <div className="relative">
      <div
        className="size-5 rounded-full flex items-center justify-center overflow-hidden"
        style={{ backgroundColor: logo?.bg ?? "transparent" }}
      >
        <PmsLogoContent logo={logo} pmsType={pmsType} />
      </div>
      <div className="absolute -bottom-1.5 -right-1.5 bg-background-800 rounded-full">
        <HugeiconsIcon
          icon={isSuccess ? CheckmarkCircle02Icon : CancelCircleIcon}
          size={16}
          className={isSuccess ? "text-success-500" : "text-danger-500"}
        />
      </div>
    </div>
  );
};

// Missed inbound call (CRM-1235): the answered-call glyph with a danger overlay,
// so a missed call is scannable in the timeline without reading the title. The
// `iconMap` above is pre-rendered JSX keyed on activity_type alone and cannot
// vary by metadata, so this case is picked in `renderActivityGlyph` instead.
const MissedInboundCallIcon = () => (
  <div className="relative">
    <HugeiconsIcon size={20} icon={CallIncoming02Icon} />
    <div className="absolute -bottom-1.5 -right-1.5 bg-background-800 rounded-full">
      <HugeiconsIcon
        icon={CancelCircleIcon}
        size={16}
        className="text-danger-500"
      />
    </div>
  </div>
);

const renderActivityGlyph = (
  activityType: string,
  metadata?: LeadActivity["metadata"]
): ReactNode => {
  if (PMS_PUSH_ACTIVITY_TYPES.has(activityType))
    return <PmsIcon activityType={activityType} pmsType={metadata?.pms_type} />;
  if (isMissedInboundCallActivity(activityType, metadata))
    return <MissedInboundCallIcon />;
  return iconMap[activityType as LeadActivityType];
};

const ACTIVITY_ICON_TONES: Partial<Record<string, string>> = {
  email_unsubscribed: "bg-primary-20 text-primary-600"
};

export const TimeLineIcon = ({
  activityType,
  actorType,
  metadata
}: {
  activityType: string;
  actorType?: string;
  metadata?: LeadActivity["metadata"];
}) => {
  return (
    <div
      className={cx(
        "relative flex items-center justify-center w-10 h-10 rounded-full",
        ACTIVITY_ICON_TONES[activityType] ?? "bg-background-900"
      )}
    >
      {renderActivityGlyph(activityType, metadata)}
      <Show if={isAiAgentActor(actorType)}>
        <img
          src={agentProfileSvg}
          alt="AI agent"
          className="absolute -top-1 -left-1 size-5"
        />
      </Show>
    </div>
  );
};
